import React, { useMemo } from "react";
import BankTransfersCategorySelect from "./BankTransfersCategorySelect";
import BankTransfersCategoryBadge from "./BankTransfersCategoryBadge";
import BankTransfersTripSelect from "./BankTransfersTripSelect";
import BankTransfersMembershipMemberSelect from "./BankTransfersMembershipMemberSelect";

const BANK_TRANSFER_CATEGORY = {
    TRIP_INCOME: 1,
    TRIP_EXPENSE: 2,
    TRIP_REFUND: 3,
    TRIP_EXPENSE_REFUND: 5,
    MEMBERSHIP_FEE: 9,
    CURRENCY_PURCHASE: 24
};

const RESERVATION_CATEGORY_IDS = [
    BANK_TRANSFER_CATEGORY.TRIP_INCOME,
    BANK_TRANSFER_CATEGORY.TRIP_REFUND
];

const TRIP_RELATED_CATEGORY_IDS = [
    BANK_TRANSFER_CATEGORY.TRIP_INCOME,
    BANK_TRANSFER_CATEGORY.TRIP_EXPENSE,
    BANK_TRANSFER_CATEGORY.TRIP_REFUND,
    BANK_TRANSFER_CATEGORY.TRIP_EXPENSE_REFUND,
    BANK_TRANSFER_CATEGORY.CURRENCY_PURCHASE
];

const getAmountClass = (matchStatus) => {
    switch (matchStatus) {
        case 0:
            return "bank-transfers-amount-unmatched";

        case 1:
        case 2:
            return "bank-transfers-amount-payment";

        case 3:
            return "bank-transfers-amount-refund";

        default:
            return "";
    }
};

const BankTransfersTable = ({
    transfers,
    categories,
    relatedTripOptions,
    onCategoryChange,
    onRelatedTripChange,
    onRelatedTripMenuOpen,
    groupByCategory,
    updatingTransferGuid,
    isLoadingTrips,
    membershipMemberOptions,
    onMembershipMemberChange,
}) => {
    const groupedTransfers = useMemo(() => {
        if (!groupByCategory)
            return null;

        return transfers.reduce((result, transfer) => {
            const key = transfer.categoryId ?? "none";

            if (!result[key]) {
                result[key] = {
                    category:
                        categories.find(
                            (c) => c.id === transfer.categoryId
                        ) ?? null,
                    transfers: []
                };
            }

            result[key].transfers.push(transfer);

            return result;
        }, {});
    }, [transfers, categories, groupByCategory]);

    const renderRows = (rows) =>
        rows.map((transfer) => {
            const isUnmatchedReservationTransfer =
                RESERVATION_CATEGORY_IDS.includes(transfer.categoryId) &&
                !transfer.reservationCode;

            return (
            <tr
                key={transfer.guid}
                className={`bank-transfers-row ${isUnmatchedReservationTransfer ? "bank-transfers-row-unmatched-reservation" : ""}`}
            >
                <td className="bank-transfers-date-cell">
                    {new Date(
                        transfer.transferDate
                    ).toLocaleDateString("pl-PL")}
                </td>

                <td
                    className={`bank-transfers-amount-cell ${getAmountClass(
                        transfer.matchStatus
                    )}`}
                >
                    {transfer.amount.toFixed(2)} zł
                </td>

                <td className="bank-transfers-title-cell">
                    {transfer.title || "—"}
                </td>

                <td className="bank-transfers-sender-cell">
                    {transfer.senderName || "—"}
                </td>

                <td className="bank-transfers-category-cell">
                    <div className="bank-transfers-category-content">
                    <BankTransfersCategorySelect
                        value={transfer.categoryId}
                        amount={transfer.amount}
                        categories={categories}
                        isLoading={
                            updatingTransferGuid === transfer.guid
                        }
                        onChange={(categoryId) =>
                            onCategoryChange(
                                transfer.guid,
                                categoryId
                            )
                        }
                    />
                    </div>
                </td>

<td className="bank-transfers-trip-cell">
    {TRIP_RELATED_CATEGORY_IDS.includes(transfer.categoryId) ? (
        <BankTransfersTripSelect
            value={transfer.relatedTripId}
            valueName={transfer.relatedTripName}
            trips={relatedTripOptions}
            isLoadingTrips={isLoadingTrips}
            isSaving={updatingTransferGuid === transfer.guid}
            onMenuOpen={onRelatedTripMenuOpen}
            onChange={(relatedTripId) => onRelatedTripChange(transfer.guid, relatedTripId)}
        />
    ) : transfer.categoryId === BANK_TRANSFER_CATEGORY.MEMBERSHIP_FEE ? (
        <BankTransfersMembershipMemberSelect
            value={transfer.membershipMemberId}
            options={membershipMemberOptions}
            disabled={updatingTransferGuid === transfer.guid}
            onChange={(membershipMemberId) => onMembershipMemberChange(transfer.guid, membershipMemberId)}
        />
    ) : (
        <span className="bank-transfer-trip-not-available">
            —
        </span>
    )}
</td>
            </tr>
            );
        });

    const renderTable = (rows) => (
        <div className="bank-transfers-table-responsive">
            <table className="bank-transfers-table">
                <thead>
                    <tr>
                        <th>Data</th>
                        <th>Kwota</th>
                        <th>Tytuł</th>
                        <th>Od/do kogo</th>
                        <th>Typ przelewu</th>
                        <th>Powiązania</th>
                    </tr>
                </thead>

                <tbody>
                    {renderRows(rows)}
                </tbody>
            </table>
        </div>
    );

    if (!groupByCategory)
        return renderTable(transfers);

    return (
        <div className="transfer-category-groups">
            {Object.entries(groupedTransfers)
                .sort(([keyA], [keyB]) => {
                    if (keyA === "none")
                        return -1;

                    if (keyB === "none")
                        return 1;

                    return Number(keyA) - Number(keyB);
                })
                .map(
                    ([
                        key,
                        {
                            category,
                            transfers: categoryTransfers
                        }
                    ]) => (
                        <section
                            key={key}
                            className="transfer-category-review-group"
                        >
                            <div className="transfer-category-review-header">
                                <div className="transfer-category-review-title">
                                    {category ? (
                                        <BankTransfersCategoryBadge
                                            category={category}
                                        />
                                    ) : (
                                        <span className="transfer-category-pill transfer-category-pill-empty">
                                            Brak kategorii
                                        </span>
                                    )}

                                    <span className="transfer-category-review-count">
                                        {categoryTransfers.length} operacji
                                    </span>
                                </div>

                                <div className="transfer-category-review-sum">
                                    {categoryTransfers
                                        .reduce(
                                            (sum, transfer) =>
                                                sum + transfer.amount,
                                            0
                                        )
                                        .toFixed(2)}{" "}
                                    zł
                                </div>
                            </div>

                            {renderTable(categoryTransfers)}
                        </section>
                    )
                )}
        </div>
    );
};

export default BankTransfersTable;